import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type { SurveyPreset } from '@citizen-walk/shared-types';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSurveyUsageCounts } from '../survey-admin';
import { useAdminAuth } from '../auth';
import { SimpleJsonMasterPage } from './SimpleJsonMasterPage';
import {
  buildSurveyPresetExport,
  buildSurveyPresetExportFilename,
  downloadPresetJson,
  parseSurveyPresetExport,
} from './surveyPresetJsonIo';
import { upsertMaster, useSurveyPresets } from './useMasters';

/**
 * M8 surveyPresets 管理画面 (docs/04 §3-1 M8、Phase 3.5 admin 第 5 弾)。
 *
 * Sprint BG-DataSettings 12 (A-S16/S17): 旧 v1 `old/apps/admin/src/pages/MasterSurveysPage.tsx`
 * の visual editor を `SurveyPresetEditPage` (個別 page) に移植し、本一覧 page には各 row 横に
 * 「✏ visual editor」リンクを表示する形にした。SimpleJsonMasterPage の JSON 編集は併用可能
 * (rule: 旧データの一括 import 等で JSON mode が役立つため温存)。
 *
 * AW8 (docs/108): 旧2 `MasterSurveysPage` 踏襲で
 *   - High3: テンプレ単位の JSON export (⬇) / import (⬆) を追加
 *   - Medium: 取込状況バッジ (collectionGroup('surveys') 集計で linked / forked 地区数)
 *
 * 廃止した点: なし (旧 JSON-only mode を残しつつ visual editor + I/O + 取込状況を追加)。
 */
export function SurveyPresetsPage() {
  const state = useSurveyPresets();
  const { state: authState } = useAdminAuth();
  const { toast } = useToast();
  const items = state.status === 'ready' ? state.items : [];

  const isMaster = authState.role === 'master';
  const uid = authState.user?.uid ?? 'admin-stub';
  const usage = useSurveyUsageCounts(isMaster);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  function handleExport(preset: SurveyPreset & { id: string }) {
    const exp = buildSurveyPresetExport(preset);
    downloadPresetJson(buildSurveyPresetExportFilename(preset), exp);
  }

  async function handleImportFile(file: File) {
    if (!isMaster) return;
    setBusy(true);
    try {
      const text = await file.text();
      const exp = parseSurveyPresetExport(text);
      await upsertMaster<SurveyPreset>({
        collectionName: 'surveyPresets',
        uid,
        isNew: true,
        data: {
          displayName: exp.preset.displayName || '取込テンプレ',
          enabled: true,
          mode: exp.preset.mode,
          title: exp.preset.title ?? '',
          description: exp.preset.description ?? '',
          status: 'draft',
          collectParticipantProfile: exp.preset.collectParticipantProfile ?? true,
          allowEdit: exp.preset.allowEdit ?? true,
          questions: exp.preset.questions,
        } as Omit<SurveyPreset, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>,
      });
      toast('success', 'テンプレを取込みました (draft として作成)');
    } catch (e) {
      toast('error', `インポート失敗: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {items.length > 0 && (
        <Card padding="md" className="mb-4 border-primary-200 bg-primary-50">
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <h2 className="text-sm font-bold text-slate-900">✏ visual editor (A-S16/S17)</h2>
            {isMaster && (
              <div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy}
                  title="JSON ファイルからテンプレを取込 (draft 作成)"
                >
                  {busy ? '取込中…' : '⬆ JSON インポート'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/json"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void handleImportFile(f);
                    e.target.value = '';
                  }}
                />
              </div>
            )}
          </div>
          <p className="text-xs text-slate-600 mb-3">
            個別 page で title / description / status / questions を視覚的に編集できます (JSON 直接
            編集は下の SimpleJsonMaster でも可能)。各テンプレの取込状況 (リンク / コピーした地区数)
            も表示します。
          </p>
          <ul className="grid grid-cols-1 gap-1">
            {items.map((p) => {
              const u = usage.counts.get(p.id);
              const linked = u?.linked ?? 0;
              const forked = u?.forked ?? 0;
              return (
                <li
                  key={p.id}
                  className="flex items-center justify-between gap-2 text-sm bg-white border border-slate-200 rounded px-3 py-1.5 flex-wrap"
                >
                  <span className="truncate min-w-0">
                    <strong>{p.displayName}</strong>
                    <span className="ml-2 text-xs text-slate-500">
                      id: {p.id} / {(p.questions as SurveyPreset['questions'])?.length ?? 0} 問
                    </span>
                  </span>
                  <span className="flex items-center gap-2 flex-wrap shrink-0">
                    {/* 取込状況バッジ (AW8 Medium) */}
                    {usage.status === 'ready' && linked === 0 && forked === 0 && (
                      <span className="text-[10px] text-slate-400">取込なし</span>
                    )}
                    {linked > 0 && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-800"
                        title="このテンプレを linked (追従) で取込済の地区数"
                      >
                        🔗 {linked} 地区
                      </span>
                    )}
                    {forked > 0 && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-warning-100 text-warning-800"
                        title="このテンプレを forked (コピー後独立) で取込済の地区数"
                      >
                        📋 {forked} 地区
                      </span>
                    )}
                    {isMaster && (
                      <button
                        type="button"
                        onClick={() => handleExport(p)}
                        title="このテンプレを JSON でエクスポート"
                        aria-label={`${p.displayName} を JSON エクスポート`}
                        className="text-xs px-2 py-1 border border-slate-300 rounded hover:bg-slate-50"
                      >
                        ⬇
                      </button>
                    )}
                    <Link
                      to={`/masters/survey-preset/${p.id}`}
                      className="text-xs text-primary-600 hover:text-primary-700 hover:underline"
                    >
                      visual editor で開く →
                    </Link>
                  </span>
                </li>
              );
            })}
          </ul>
          {usage.status === 'error' && (
            <p className="text-[11px] text-slate-400 mt-2">
              取込状況の集計に失敗しました (権限 / index)。バッジは表示されません。
            </p>
          )}
        </Card>
      )}

      <SimpleJsonMasterPage<SurveyPreset>
        collectionName="surveyPresets"
        title="アンケートテンプレ"
        subtitle="アンケートのテンプレ (内部 ID: surveyPresets、docs/04 §3-1 M8、Sprint BG-DataSettings 12 で visual editor 追加)。"
        jsonFieldName="questions"
        jsonFieldLabel="questions"
        jsonFieldPlaceholder='[{ "id": "q1", "type": "short_text", "title": "...", "required": false }]'
        newIdPlaceholder="例: ecw-default-survey"
        state={state}
      />
    </>
  );
}
